import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { isGroupLeague, defaultStagePhase, prioritiesForNewStages } from '../utilities/stagePriority';
import StagePhasePicker from './StagePhasePicker';
import '../styles/createMatchModal.css';

// stages — стадии сезона, format — тип лиги и «Туров, идущих в группу» из настроек сезона.
// В лиге с группами админ выбирает этап, номер стадии считается сам
// (utilities/stagePriority): групповой тур никогда не уезжает в плей-офф
function CreateStageModal({ isOpen, onClose, onSuccess, league, season, stages = [], format = null }) {
  const dispatch = useDispatch();

  const [stageName, setStageName] = useState('');
  const [stageShortName, setStageShortName] = useState('');
  const [phase, setPhase] = useState('group');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Каждое открытие — этап по умолчанию: пока нет плей-офф, групповой
  useEffect(() => {
    if (isOpen) setPhase(defaultStagePhase(stages, format));
  }, [isOpen, stages, format]);

  // Без настроек сезона нельзя понять, куда попадёт стадия
  const formatReady = Boolean(format);
  const priorityNumber = prioritiesForNewStages(stages, format, phase, 1)[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stageName.trim()) {
      setMessage({ text: 'Введите название стадии', type: 'error' });
      return;
    }
    if (!formatReady) {
      setMessage({ text: 'Настройки сезона ещё не загрузились — попробуйте через секунду', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // priority посчитан по этапу: групповой тур — не дальше «Туров в группе»
      const stageData = {
        name: stageName.trim(),
        short_name: stageShortName.trim() || null,
        priority: priorityNumber,
        group_id: null,
        league_id: parseInt(league.value),
        season_id: parseInt(season.value),
        stages_amount: 1,
        matches: []
      };

      const response = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/addStage`,
        stageData,
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      setMessage({ text: 'Стадия успешно создана!', type: 'success' });
      setTimeout(() => {
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating stage:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Ошибка при создании стадии', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStageName('');
    setStageShortName('');
    setMessage({ text: '', type: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Создание стадии</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section-modal">
              <label className="form-label-modal form-label-required">
                Название стадии
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                placeholder="Например: 1 Тур, Полуфинал, Группа А"
                maxLength={100}
              />
            </div>

            <div className="form-section-modal">
              <label className="form-label-modal">
                Короткое название (опционально)
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageShortName}
                onChange={(e) => setStageShortName(e.target.value)}
                placeholder="Например: 1Т, ПФ"
                maxLength={50}
              />
            </div>

            <StagePhasePicker format={format} phase={phase} onChange={setPhase} disabled={loading} />
            <div className="form-section-modal">
              <small style={{ color: '#718096', fontSize: '13px' }}>
                {!formatReady
                  ? 'Загружаем настройки сезона…'
                  : isGroupLeague(format)
                    ? `Номер стадии: ${priorityNumber}. ${phase === 'group' ? 'Матчи пойдут в таблицу групп.' : 'Стадия пойдёт в плей-офф.'}`
                    : `Номер стадии: ${priorityNumber}.`}
              </small>
            </div>

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading || !formatReady}
                className="btn-modal btn-primary-modal"
              >
                {loading ? 'Создание...' : 'Создать стадию'}
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateStageModal;
